/**
 * Contact Work lanes, letter packets, and returned-mail retry cards.
 */
import { formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize, pill, shortUri } from "./dashboard-html.js";
import { isReturnedMailRetryLetterTask } from "./dashboard-command-center.js";
import { contactWorkAssignableTaskIds, isContactWorkTaskActionable } from "./dashboard-contact-work.js";
import { contactWorkActionFormHtml } from "./dashboard-contact-policy.js";
import type { LeadFactoryContactLetterPacket, LeadFactoryTask } from "./dashboard-lead-factory-types.js";

export function prioritizeFocusedLeadTasks(tasks: LeadFactoryTask[], focusedLeadId: string): LeadFactoryTask[] {
  const leadId = focusedLeadId.trim();
  if (!leadId) return tasks;
  return [...tasks].sort((left, right) => {
    const leftFocused = left.property_lead_id === leadId ? 0 : 1;
    const rightFocused = right.property_lead_id === leadId ? 0 : 1;
    return leftFocused - rightFocused;
  });
}

export function contactWorkLaneHtml(label: string, tasks: LeadFactoryTask[], packets: LeadFactoryContactLetterPacket[] = [], focusedLeadId = ""): string {
  const packetsByTask = leadFactoryPacketsByTask(packets);
  const visibleTasks = prioritizeFocusedLeadTasks(tasks, focusedLeadId).slice(0, 4);
  const isRetryLetterLane = label === "Retry Letters";
  const isLetterLane = label === "Letters" || isRetryLetterLane;
  const assignableTaskIds = contactWorkAssignableTaskIds(tasks);
  return `
    <section class="contact-lane" ${isRetryLetterLane ? "data-contact-work-retry-letter-lane" : ""}>
      <div class="lane-heading">
        <strong>${escapeHtml(label)}</strong>
        <span>${formatInteger(tasks.length)}</span>
        <button class="button secondary" type="button" data-contact-work-bulk-assign="${escapeAttr(assignableTaskIds.join(","))}" data-contact-work-bulk-label="${escapeAttr(label)}" ${assignableTaskIds.length === 0 ? "disabled" : ""}>Assign lane</button>
      </div>
      ${isLetterLane ? contactLetterBatchPanelHtml(tasks) : ""}
      <div class="task-list">
        ${visibleTasks.map((task) => contactWorkTaskHtml(task, packetsByTask)).join("") || `<p class="empty">No work.</p>`}
      </div>
    </section>
  `;
}

export function contactLetterBatchPanelHtml(tasks: LeadFactoryTask[]): string {
  const visibleTaskCount = tasks.slice(0, 4).filter((task) => task.property_lead_id && task.task_type === "prepare_letter").length;
  return `
    <div class="letter-batch-panel" data-contact-letter-batch-panel>
      <input type="text" maxlength="160" value="Lead Factory acquisitions team" aria-label="Batch letter sender" data-letter-batch-sender>
      <input type="text" maxlength="180" value="Generated visible Contact Work mail batch." aria-label="Batch letter note" data-letter-batch-note>
      <button class="button secondary" type="button" data-contact-letter-batch-generate ${visibleTaskCount === 0 ? "disabled" : ""}>Generate visible packets</button>
      <button class="button secondary" type="button" data-contact-letter-batch-manifest>Create mail manifest</button>
      <button class="button secondary" type="button" data-contact-letter-batch-handoff disabled>Create vendor handoff</button>
      <button class="button secondary" type="button" data-contact-letter-batch-send>Record visible sent</button>
      <button class="button secondary" type="button" data-contact-letter-batch-returned disabled>Record visible returned</button>
      <span class="status-inline" data-contact-letter-batch-status></span>
      <small>${formatInteger(visibleTaskCount)} visible letter tasks</small>
    </div>
  `;
}

export function leadFactoryPacketsByTask(packets: LeadFactoryContactLetterPacket[]): Map<string, LeadFactoryContactLetterPacket[]> {
  const byTask = new Map<string, LeadFactoryContactLetterPacket[]>();
  for (const packet of packets) {
    const taskId = packet.task_id?.trim();
    if (!taskId) continue;
    const list = byTask.get(taskId) ?? [];
    list.push(packet);
    byTask.set(taskId, list);
  }
  for (const list of byTask.values()) {
    list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  }
  return byTask;
}

export function contactWorkTaskHtml(task: LeadFactoryTask, packetsByTask: Map<string, LeadFactoryContactLetterPacket[]> = new Map()): string {
  const leadLabel = task.property_address || task.owner_name || task.property_lead_id || "Contact lead";
  const packets = task.task_type === "prepare_letter" ? packetsByTask.get(task.id) ?? [] : [];
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(leadLabel)}</strong>
        <small>${escapeHtml(labelize(task.task_type))} · ${escapeHtml(task.county || "unknown county")}</small>
        <p>${escapeHtml(task.instructions)}</p>
        ${contactWorkRetryLetterHtml(task)}
        ${contactWorkReturnedMailRemediationHtml(task)}
      </div>
      ${task.property_lead_id && task.task_type === "prepare_letter" ? contactLetterPacketFormHtml(task) : ""}
      ${task.task_type === "prepare_letter" ? contactLetterPacketHistoryHtml(packets) : ""}
      ${task.property_lead_id && isContactWorkTaskActionable(task) ? contactWorkActionFormHtml(task) : contactWorkFallbackActionsHtml(task)}
    </article>
  `;
}

export function contactWorkRetryLetterHtml(task: LeadFactoryTask): string {
  if (!isReturnedMailRetryLetterTask(task)) return "";
  const returnedPacketCount = Number(task.returned_mail_packet_count ?? 0);
  const batchId = task.latest_returned_mail_batch_id?.trim() || "";
  const packetId = task.latest_returned_mail_packet_id?.trim() || "";
  const retryAddress = task.retry_mailing_address?.trim() || "";
  const retryAddressSource = task.retry_mailing_contact_source?.trim() || "";
  const retryAddressContactId = task.retry_mailing_contact_id?.trim() || "";
  const retryAddressStatus = task.retry_mailing_contact_status?.trim() || "";
  const retryAddressVerified = retryAddressStatus === "verified";
  const artifactUrl = batchId ? `/dashboard/lead-factory/contact-returned-mail-batches/${encodeURIComponent(batchId)}/artifact` : "";
  const countLabel = returnedPacketCount > 0 ? `${formatInteger(returnedPacketCount)} returned packet${returnedPacketCount === 1 ? "" : "s"}` : "returned-mail remediation";
  return `
    <div class="returned-mail-remediation" data-contact-work-retry-letter data-returned-mail-batch-id="${escapeAttr(batchId)}" data-returned-mail-packet-id="${escapeAttr(packetId)}">
      <strong>Returned-mail retry letter</strong>
      <small>${escapeHtml(countLabel)}${task.latest_returned_mail_at ? ` · ${escapeHtml(task.latest_returned_mail_at)}` : ""}</small>
      <p>Use the remediated mailing address; this is not a repeat of the failed mail route.</p>
      <p data-contact-work-retry-letter-address>${retryAddress ? `Remediated address: ${escapeHtml(retryAddress)}` : "No remediated mailing address is linked yet."}</p>
      <p data-contact-work-retry-letter-address-status>Address verification: ${escapeHtml(labelize(retryAddressStatus || "unverified"))}</p>
      ${
        retryAddress || retryAddressSource || retryAddressContactId || retryAddressStatus
          ? `<small>${retryAddressSource ? `Address source: ${escapeHtml(retryAddressSource)}` : "Address source: not recorded"}${retryAddressContactId ? ` · Contact: ${escapeHtml(retryAddressContactId)}` : ""}${retryAddressStatus ? ` · Status: ${escapeHtml(labelize(retryAddressStatus))}` : ""}</small>`
          : ""
      }
      ${
        task.property_lead_id && retryAddressContactId && !retryAddressVerified
          ? `<button class="button secondary" type="button" data-contact-work-retry-address-verify data-lead-id="${escapeAttr(task.property_lead_id)}" data-contact-method-id="${escapeAttr(retryAddressContactId)}">Mark address verified</button>`
          : ""
      }
      ${artifactUrl ? `<button class="button secondary" type="button" data-returned-mail-batch-download="${escapeAttr(batchId)}" data-returned-mail-batch-url="${escapeAttr(artifactUrl)}">Download return batch</button>` : ""}
    </div>
  `;
}

export function contactWorkReturnedMailRemediationHtml(task: LeadFactoryTask): string {
  const returnedPacketCount = Number(task.returned_mail_packet_count ?? 0);
  if (task.task_type !== "skip_trace" || (task.blocking_reason !== "returned_mail" && returnedPacketCount <= 0)) return "";
  const batchId = task.latest_returned_mail_batch_id?.trim() || "";
  const packetId = task.latest_returned_mail_packet_id?.trim() || "";
  const artifactUrl = batchId ? `/dashboard/lead-factory/contact-returned-mail-batches/${encodeURIComponent(batchId)}/artifact` : "";
  const countLabel = returnedPacketCount > 0 ? `${formatInteger(returnedPacketCount)} returned packet${returnedPacketCount === 1 ? "" : "s"}` : "returned mail";
  return `
    <div class="returned-mail-remediation" data-contact-work-returned-mail data-returned-mail-batch-id="${escapeAttr(batchId)}" data-returned-mail-packet-id="${escapeAttr(packetId)}">
      <strong>Returned-mail remediation</strong>
      <small>${escapeHtml(countLabel)}${task.latest_returned_mail_at ? ` · ${escapeHtml(task.latest_returned_mail_at)}` : ""}</small>
      <p>Use skip trace to replace the failed mailing route before the next letter attempt.</p>
      ${artifactUrl ? `<button class="button secondary" type="button" data-returned-mail-batch-download="${escapeAttr(batchId)}" data-returned-mail-batch-url="${escapeAttr(artifactUrl)}">Download return batch</button>` : ""}
    </div>
  `;
}

export function contactLetterPacketFormHtml(task: LeadFactoryTask): string {
  return `
    <form class="structured-action-form contact-letter-packet-form" data-contact-letter-packet-form data-lead-id="${escapeAttr(task.property_lead_id || "")}" data-contact-task-id="${escapeAttr(task.id)}">
      <input type="text" maxlength="160" value="${escapeAttr(task.property_address || "")}" aria-label="Letter packet property" data-letter-packet-property readonly>
      <input type="text" maxlength="160" value="${escapeAttr(task.owner_name || "")}" aria-label="Letter packet owner" data-letter-packet-owner readonly>
      <input type="text" maxlength="160" value="Lead Factory acquisitions team" aria-label="Letter sender" data-letter-packet-sender>
      <input type="text" maxlength="180" value="Generated printable owner letter packet from CRM evidence." aria-label="Letter packet note" data-letter-packet-note>
      <button class="button secondary" type="submit">Generate letter packet</button>
    </form>
  `;
}

export function contactLetterPacketHistoryHtml(packets: LeadFactoryContactLetterPacket[]): string {
  if (packets.length === 0) {
    return `<div class="packet-history" data-contact-letter-packet-history><p class="empty">No letter packet generated yet.</p></div>`;
  }
  return `
    <div class="packet-history" data-contact-letter-packet-history>
      ${packets.slice(0, 3).map(contactLetterPacketRowHtml).join("")}
    </div>
  `;
}

export function contactLetterPacketRowHtml(packet: LeadFactoryContactLetterPacket): string {
  const artifactUrl = `/dashboard/lead-factory/contact-letter-packets/${encodeURIComponent(packet.id)}/artifact`;
  return `
    <article class="packet-row" data-contact-letter-packet-row data-letter-packet-id="${escapeAttr(packet.id)}" data-letter-packet-status="${escapeAttr(packet.status)}">
      <div>
        <strong>${escapeHtml(packet.title || "Owner Letter Packet")}</strong>
        <small>${escapeHtml(packet.status)} · ${escapeHtml(packet.created_at || "unknown time")}</small>
        <p><code title="${escapeAttr(packet.artifact_uri)}">${escapeHtml(shortUri(packet.artifact_uri || ""))}</code></p>
      </div>
      <div class="inline-actions">
        <button class="button secondary" type="button" data-letter-packet-download="${escapeAttr(packet.id)}" data-letter-packet-url="${escapeAttr(artifactUrl)}">Download</button>
        <button class="button secondary" type="button" data-letter-packet-print="${escapeAttr(packet.id)}" data-letter-packet-url="${escapeAttr(artifactUrl)}">Print</button>
        ${pill(labelize(packet.status), packet.status)}
      </div>
    </article>
  `;
}

export function contactWorkFallbackActionsHtml(task: LeadFactoryTask): string {
  const campaignActionability = task.campaign_actionability || "ready";
  if (campaignActionability !== "ready") {
    return `
      <div class="inline-actions" data-contact-work-waiting-actionability="${escapeAttr(campaignActionability)}" data-contact-task-id="${escapeAttr(task.id)}">
        ${task.property_lead_id ? `<button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(task.property_lead_id)}">Detail</button>` : ""}
        ${pill(`Waiting: ${labelize(campaignActionability)}`, "blocked")}
      </div>
    `;
  }
  return `
    <div class="inline-actions">
      ${task.property_lead_id ? `<button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(task.property_lead_id)}">Detail</button>` : ""}
      <button class="button secondary" type="button" data-task-complete="${escapeAttr(task.id)}">Complete</button>
    </div>
  `;
}

